/**
 * Vanilla-DOM implementation of `UiShell`.
 *
 * The shell owns no application state. It renders three stacked layers — camera video,
 * transparent overlay canvas, chrome — and projects the view models it is handed. The only
 * private state it keeps is presentational: whether the settings drawer is open, whether the
 * secondary transport row is expanded, whether the reset button is armed, and per-element
 * caches so the per-frame setters can skip DOM writes.
 *
 * The settings drawer is deliberately two-tier. Everything a first-time player needs is
 * plain language and three or four choices wide; the exact numbers that used to be the whole
 * panel still exist, one disclosure down, for anyone who wants them.
 */

import './styles.css';

import { STATE_COPY } from '../app/AppStateMachine';
import type { AppErrorInfo, AppState } from '../app/AppStateMachine';
import { DEFAULT_SETTINGS, SETTINGS_LIMITS } from '../app/settings';
import type { PlacementSettings, Settings, VibratoMode } from '../app/settings';
import { classNames, el, formatHz, formatMs, svgIcon } from './dom';
import type { ElChild, IconName } from './dom';
import type {
  CalibrationViewModel,
  DiagnosticsSnapshot,
  HandTag,
  LiveReadout,
  UiCallbacks,
  UiCapabilities,
  UiShell,
  UiSurfaces,
} from './uiTypes';

/** States in which the persistent chrome (readout + transport) is shown. */
const CHROME_STATES: ReadonlySet<AppState> = new Set<AppState>([
  'placement',
  'calibrating',
  'ready',
  'playing',
  'noHands',
]);

type PanelKind = 'intro' | 'loading' | 'permission' | 'error';

/** `cameraUnsupported` reuses the error panel: same shape, same recovery affordance. */
const PANEL_FOR_STATE: Readonly<Record<AppState, PanelKind | null>> = {
  intro: 'intro',
  requestingCamera: 'loading',
  loadingModel: 'loading',
  placement: null,
  calibrating: null,
  ready: null,
  playing: null,
  noHands: null,
  permissionDenied: 'permission',
  cameraUnsupported: 'error',
  error: 'error',
};

/** `STATE_COPY`, with the few overrides the shell's larger panels want. */
const COPY: Readonly<Record<AppState, { title: string; body: string }>> = {
  ...STATE_COPY,
  intro: {
    title: 'Virtual Theremin',
    body:
      'Play a theremin in the air. The camera watches your hands: one hand shapes the pitch, ' +
      'the other shapes the volume. No extra hardware and no account.',
  },
  requestingCamera: {
    title: 'Waiting for camera permission',
    body: 'Your browser is asking for camera access. Choose Allow to continue.',
  },
  loadingModel: {
    title: 'Loading hand tracking',
    body: 'Preparing the on-device hand-tracking model. This runs entirely in your browser.',
  },
};

const PRIVACY_TEXT =
  'Your camera image never leaves this device. Frames are processed locally in your browser; ' +
  'nothing is uploaded.';
const MICROPHONE_TEXT =
  'The microphone is never requested. The instrument only produces sound, it never listens.';
const PERMISSION_HELP =
  'Open the padlock or camera icon in the address bar, set Camera to Allow, then try again. ' +
  'On iOS check Settings, Safari, Camera. Some browsers need the page reloaded afterwards.';
const PLACEMENT_HINT = 'Drag to move · pinch or scroll to resize · two-finger twist to rotate';

const RESET_ARM_MS = 4000;
const TOAST_MS = 4000;
const UNIT_RANGE = { min: 0, max: 1 };

/** Keys of `Settings` whose value is assignable to `T`; keeps the binders type-safe. */
type SettingKey<T> = {
  [K in keyof Settings]-?: Settings[K] extends T ? K : never;
}[keyof Settings];

interface Bounds {
  min: number;
  max: number;
}

const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

const div = (cls: string, ...kids: ElChild[]): HTMLDivElement => el('div', { class: cls }, ...kids);
const sp = (cls: string, ...kids: ElChild[]): HTMLSpanElement =>
  el('span', { class: cls }, ...kids);
const unit = (text: string): HTMLSpanElement => sp('unit', text);

function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

function setPressed(button: HTMLButtonElement, pressed: boolean): void {
  button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
}

/** For the two buttons that reveal another region rather than toggling a state. */
function setExpanded(button: HTMLButtonElement, expanded: boolean): void {
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

/**
 * A single text slot that only touches the DOM when its value actually changes.
 * `format` runs on change only, so the per-frame path allocates nothing while values hold.
 */
function cell(node: HTMLElement, format: (value: number) => string = String): (v: number) => void {
  let last = Number.NaN;
  return (value) => {
    if (value === last) return;
    last = value;
    node.textContent = format(value);
  };
}

function textCell(node: HTMLElement): (value: string) => void {
  let last: string | null = null;
  return (value) => {
    if (value === last) return;
    last = value;
    node.textContent = value;
  };
}

interface Btn {
  button: HTMLButtonElement;
  label: HTMLSpanElement;
}

function makeButton(testid: string, text: string, icon: IconName | null, extra?: string): Btn {
  const label = sp('btn__label', text);
  // The label is visually hidden on the compact rows, so the accessible name has to survive
  // independently of it.
  const attrs = { type: 'button', title: text, 'aria-label': text };
  const cls = classNames('btn', extra);
  const button = el('button', { testid, class: cls, attrs }, icon && svgIcon(icon), label);
  return { button, label };
}

// --- settings drawer field factories -----------------------------------------
// The test id doubles as the element id, so every label/control pair is associated by
// construction and there is only one string to keep in sync. Every field can carry a hint:
// one plain-language sentence saying what the control does to the sound or the feel.

interface Field {
  row: HTMLElement;
  input: HTMLInputElement;
}
interface RangeField extends Field {
  output: HTMLOutputElement;
}
interface SegmentField {
  row: HTMLElement;
  inputs: readonly HTMLInputElement[];
  hint: HTMLElement | null;
}

function hintNode(text: string | undefined): HTMLElement | null {
  return text === undefined ? null : el('p', { class: 'field__hint', text });
}

function checkField(testid: string, text: string, hint?: string): Field {
  const input = el('input', { id: testid, testid, class: 'check', attrs: { type: 'checkbox' } });
  const row = el('label', { class: 'field field--check', attrs: { for: testid } });
  row.append(input, sp('field__label', text));
  const note = hintNode(hint);
  if (!note) return { row, input };
  return { row: div('field__group', row, note), input };
}

function rangeField(
  testid: string,
  text: string,
  bounds: Bounds,
  step: number,
  hint?: string,
): RangeField {
  const attrs = { type: 'range', min: bounds.min, max: bounds.max, step };
  const input = el('input', { id: testid, testid, class: 'range', attrs });
  const output = el('output', { class: 'field__value', attrs: { for: testid } });
  const label = el('label', { class: 'field__label', text, attrs: { for: testid } });
  const row = div('field field--range', label, output, input, hintNode(hint));
  return { row, input, output };
}

function numberField(testid: string, text: string, bounds: Bounds, suffix: string): Field {
  const attrs = { type: 'number', min: bounds.min, max: bounds.max, step: 1, inputmode: 'numeric' };
  const input = el('input', { id: testid, testid, class: 'number', attrs });
  const label = el('label', { class: 'field__label', text, attrs: { for: testid } });
  const row = div('field field--number', label, sp('field__control', input, unit(suffix)));
  return { row, input };
}

function segmentField(
  testid: string,
  text: string,
  options: readonly { value: string; label: string }[],
  hint?: string,
): SegmentField {
  const items = options.map((option) => {
    const id = `${testid}-${option.value}`;
    const attrs = { type: 'radio', name: testid, value: option.value };
    const input = el('input', { id, class: 'segment__input', attrs });
    const label = el('label', { text: option.label, attrs: { for: id } });
    return { input, wrap: sp('segment__item', input, label) };
  });
  const legend = el('legend', { class: 'field__label', text });
  const segment = div('segment', ...items.map((item) => item.wrap));
  const note = hintNode(hint);
  const row = el('fieldset', { class: 'field field--segment', testid }, legend, segment, note);
  return { row, inputs: items.map((item) => item.input), hint: note };
}

/**
 * Named pitch ranges, so choosing where the instrument sings does not require knowing that
 * 110 Hz is an A. The exact numbers are still there under Advanced.
 */
const PITCH_PRESETS: readonly { value: string; label: string; min: number; max: number }[] = [
  { value: 'low', label: 'Warm', min: 65, max: 1046 },
  { value: 'standard', label: 'Classic', min: 110, max: 1760 },
  { value: 'wide', label: 'Wide', min: 82, max: 2637 },
  { value: 'bright', label: 'Bright', min: 220, max: 3520 },
];

/** Named smoothing amounts. Higher smoothing is steadier but arrives a few ms later. */
const RESPONSE_PRESETS: readonly { value: string; label: string; smoothing: number }[] = [
  { value: 'quick', label: 'Quick', smoothing: 0.25 },
  { value: 'balanced', label: 'Balanced', smoothing: 0.5 },
  { value: 'steady', label: 'Steady', smoothing: 0.78 },
];

const VIBRATO_OPTIONS: readonly { value: VibratoMode; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'gentle', label: 'Gentle' },
  { value: 'rich', label: 'Rich' },
  { value: 'hand', label: 'Open hand' },
];

const SHORTCUTS: readonly [string, string][] = [
  ['Space', 'Start or stop'],
  ['M', 'Mute'],
  ['S', 'Swap hands'],
  ['C', 'Calibrate'],
  ['D', 'Diagnostics'],
  ['Esc', 'Close panels'],
];

/** Which preset, if any, the current settings correspond to. '' means a custom value. */
function pitchPresetOf(settings: Readonly<Settings>): string {
  const match = PITCH_PRESETS.find(
    (preset) =>
      Math.abs(preset.min - settings.minFrequency) < 0.5 &&
      Math.abs(preset.max - settings.maxFrequency) < 0.5,
  );
  return match?.value ?? '';
}

function responsePresetOf(settings: Readonly<Settings>): string {
  const match = RESPONSE_PRESETS.find(
    (preset) => Math.abs(preset.smoothing - settings.smoothing) < 0.02,
  );
  return match?.value ?? '';
}

export function createUiShell(container: HTMLElement, callbacks: UiCallbacks): UiShell {
  const cleanups: (() => void)[] = [];
  const on = <K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    type: K,
    handler: () => void,
  ): void => {
    target.addEventListener(type, handler);
    cleanups.push(() => target.removeEventListener(type, handler));
  };

  // --- stage -----------------------------------------------------------------
  const ariaHidden = { 'aria-hidden': 'true' };
  const video = el('video', { class: 'stage__video', testid: 'camera-video', attrs: ariaHidden });
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  // Attributes as well as properties: some engines only honour the markup form for autoplay.
  for (const flag of ['autoplay', 'muted', 'playsinline', 'webkit-playsinline']) {
    video.setAttribute(flag, '');
  }
  const canvas = el('canvas', {
    class: 'stage__canvas',
    testid: 'scene-canvas',
    attrs: ariaHidden,
  });
  const handTagNodes: Record<HandTag['role'], HTMLElement> = {
    pitch: el('span', { class: 'handtag handtag--pitch', testid: 'hand-tag-pitch', text: 'Pitch' }),
    volume: el('span', {
      class: 'handtag handtag--volume',
      testid: 'hand-tag-volume',
      text: 'Volume',
    }),
  };
  handTagNodes.pitch.hidden = true;
  handTagNodes.volume.hidden = true;
  const tagLayer = div('stage__tags', handTagNodes.pitch, handTagNodes.volume);
  tagLayer.setAttribute('aria-hidden', 'true');
  const stage = el('div', { class: 'stage', testid: 'stage' }, video, canvas, tagLayer);

  // --- readout ---------------------------------------------------------------
  const noteEl = el('span', { class: 'readout__note', testid: 'note-name', text: '—' });
  const centsEl = el('span', { class: 'readout__cents', testid: 'note-cents', text: '0' });
  const tuner = div('tuner', el('i', { class: 'tuner__needle' }));
  const freqEl = el('span', { class: 'readout__hz', testid: 'frequency-value', text: '0.0' });
  const volumeEl = el('span', { class: 'readout__vol', testid: 'volume-value', text: '0' });
  const meter = div('meter', el('i', { class: 'meter__fill' }));
  const vibratoMeter = div('meter meter--vibrato', el('i', { class: 'meter__fill' }));
  const vibratoRow = div('readout__row readout__row--meter readout__row--vibrato', vibratoMeter);
  vibratoRow.append(sp('readout__tag', 'vibrato'));
  const pitchPill = el('span', { class: 'pill pill--pitch', testid: 'pitch-hand-pill' });
  const volumePill = el('span', { class: 'pill pill--volume', testid: 'volume-hand-pill' });
  const handCountEl = el('span', { class: 'readout__count', testid: 'hand-count', text: '0' });
  pitchPill.textContent = 'Pitch hand';
  volumePill.textContent = 'Volume hand';

  const readout = el(
    'section',
    {
      class: 'readout surface',
      testid: 'readout',
      // Changes every frame, so it must never be announced.
      attrs: { 'aria-live': 'off', 'aria-label': 'Live pitch and volume readout' },
    },
    div('readout__top', noteEl, div('readout__tune', centsEl, unit('cents'), tuner)),
    div('readout__row', freqEl, unit('Hz')),
    div('readout__row readout__row--meter', meter, volumeEl, unit('%')),
    vibratoRow,
    div(
      'readout__pills',
      pitchPill,
      volumePill,
      sp('pill pill--count', handCountEl, unit('hands')),
    ),
  );

  // --- diagnostics -----------------------------------------------------------
  const diagValue = (testid: string): HTMLElement =>
    el('span', { class: 'diag__value', testid, text: '-' });
  const diagRow = (text: string, value: HTMLElement): HTMLElement =>
    div('diag__row', sp('diag__label', text), value);

  const dRenderFps = diagValue('diag-render-fps');
  const dInferenceFps = diagValue('diag-inference-fps');
  const dInferenceMs = diagValue('diag-inference-ms');
  const dResolution = diagValue('diag-resolution');
  const dHands = diagValue('diag-hands');
  const dTier = diagValue('diag-tier');
  const dMode = diagValue('diag-mode');
  const dTargetHz = diagValue('diag-target-hz');
  const dLatency = diagValue('diag-latency');
  const dDropped = diagValue('diag-dropped');

  const diagnostics = el(
    'section',
    { class: 'diagnostics surface', testid: 'diagnostics-panel', attrs: { 'aria-live': 'off' } },
    el('h2', { class: 'section__title', text: 'Diagnostics' }),
    diagRow('Render fps', dRenderFps),
    diagRow('Inference fps', dInferenceFps),
    diagRow('Inference ms', dInferenceMs),
    diagRow('Camera', dResolution),
    diagRow('Hands', dHands),
    diagRow('Tier', dTier),
    diagRow('Mode', dMode),
    diagRow('Target Hz', dTargetHz),
    diagRow('Audio latency ms', dLatency),
    diagRow('Dropped frames', dDropped),
  );

  // --- transport -------------------------------------------------------------
  // Two rows: the four things a player reaches for mid-performance, and everything else
  // behind one disclosure. A row of eleven equal buttons is a menu, not an instrument.
  const startBtn = makeButton('start-instrument', 'Start', 'play', 'btn--primary');
  // The one button that keeps its words on a phone keeps only the first of them.
  startBtn.label.append(sp('btn__label-rest', ' instrument'));
  startBtn.button.title = 'Start instrument';
  startBtn.button.setAttribute('aria-label', 'Start instrument');
  const stopBtn = makeButton('stop-instrument', 'Stop', 'stop');
  const muteBtn = makeButton('mute-toggle', 'Mute', 'sound');
  const swapBtn = makeButton('swap-hands', 'Swap hands', 'swap');
  const settingsBtn = makeButton('settings-toggle', 'Settings', 'gear');
  const moreBtn = makeButton('more-toggle', 'More', 'dots');

  const switchBtn = makeButton('switch-camera', 'Switch camera', 'camera');
  const repositionBtn = makeButton('reposition', 'Reposition', 'move', 'needs-model');
  const calibrateBtn = makeButton('calibrate', 'Calibrate', 'target', 'needs-model');
  const diagBtn = makeButton('diagnostics-toggle', 'Diagnostics', 'gauge');
  const xrBtn = makeButton('enter-xr', 'Enter AR', 'cube');
  const resetBtn = makeButton('reset-all', 'Reset', 'reset');

  const transport = el(
    'nav',
    { class: 'transport surface', testid: 'transport', attrs: { 'aria-label': 'Instrument' } },
    startBtn.button,
    stopBtn.button,
    muteBtn.button,
    swapBtn.button,
    settingsBtn.button,
    moreBtn.button,
  );
  const transportMore = el(
    'nav',
    {
      class: 'transport transport--more surface',
      testid: 'transport-more',
      attrs: { 'aria-label': 'More controls' },
    },
    switchBtn.button,
    repositionBtn.button,
    calibrateBtn.button,
    diagBtn.button,
    resetBtn.button,
  );

  // --- placement hint (non-blocking: it must never cover the stage) ----------
  const placementDone = makeButton('confirm-placement', 'Done', null, 'btn--primary');
  const placementReset = makeButton('reset-placement', 'Reset', null);
  const hintBar = el(
    'div',
    { class: 'hint surface', attrs: { role: 'status' } },
    sp('hint__text', PLACEMENT_HINT),
    sp('hint__actions', placementDone.button, placementReset.button),
  );

  // --- calibration sheet -----------------------------------------------------
  const calibTitleText = STATE_COPY.calibrating.title;
  const calibTitle = el('h2', {
    id: 'calibration-title',
    class: 'section__heading',
    testid: 'calibration-title',
    text: calibTitleText,
  });
  const calibStep = sp('sheet__step');
  const calibInstruction = el('p', {
    class: 'sheet__body',
    testid: 'calibration-instruction',
    text: STATE_COPY.calibrating.body,
  });
  const calibProgress = el(
    'div',
    {
      class: 'progress',
      testid: 'calibration-progress',
      attrs: { role: 'progressbar', 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': 0 },
    },
    el('i', { class: 'progress__fill' }),
  );
  const calibSamples = sp('sheet__samples', '0 samples');
  const calibAdvance = makeButton('calibration-advance', 'Continue', null, 'btn--primary');
  const calibCancel = makeButton('calibration-cancel', 'Skip', null);
  const calibration = el(
    'section',
    {
      class: 'sheet surface',
      testid: 'calibration-panel',
      // Not modal: the player has to keep seeing themselves while they sweep.
      attrs: { role: 'dialog', 'aria-modal': 'false', 'aria-labelledby': 'calibration-title' },
    },
    div('sheet__head', calibTitle, calibStep),
    calibInstruction,
    calibProgress,
    div('sheet__foot', calibSamples, sp('sheet__actions', calibCancel.button, calibAdvance.button)),
  );

  const noHandsBadge = el('div', {
    class: 'badge',
    testid: 'no-hands-badge',
    text: 'No hands detected — hold your hands up',
    attrs: { role: 'status' },
  });
  const toast = el('div', {
    class: 'toast',
    testid: 'toast',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });

  // --- settings drawer -------------------------------------------------------
  const lim = SETTINGS_LIMITS;
  const fMasterVolume = rangeField('setting-master-volume', 'Volume', UNIT_RANGE, 0.01);
  const fVibrato = segmentField(
    'setting-vibrato',
    'Vibrato',
    VIBRATO_OPTIONS,
    'Open hand: spread the fingers of your pitch hand to make the note sing, close them into ' +
      'a fist for a flat, pure tone.',
  );
  const fPitchPreset = segmentField(
    'setting-pitch-range',
    'Pitch range',
    PITCH_PRESETS.map((preset) => ({ value: preset.value, label: preset.label })),
    'How high and low the instrument can sing.',
  );
  const fResponse = segmentField(
    'setting-response',
    'Response',
    RESPONSE_PRESETS.map((preset) => ({ value: preset.value, label: preset.label })),
    'Steady irons out shaky hands; quick follows every twitch.',
  );
  const fSensitivity = rangeField(
    'setting-sensitivity',
    'Movement range',
    lim.sensitivity,
    0.05,
    'Low means big arm movements, high means small wrist movements.',
  );
  const fSwapHands = checkField(
    'setting-swap-hands',
    'Swap pitch and volume hands',
    'Use this if the instrument has picked the wrong hand for the wrong job.',
  );
  const fSkeleton = checkField(
    'setting-show-skeleton',
    'Show hand skeleton',
    'Draws what the camera sees, coloured by the job each hand is doing.',
  );
  const fMirror = checkField('setting-mirror-preview', 'Mirror preview');
  const fReducedMotion = checkField('setting-reduced-motion', 'Reduced motion');

  const fInvertPitch = checkField('setting-invert-pitch', 'Invert pitch');
  const fInvertVolume = checkField('setting-invert-volume', 'Invert volume');
  const fControlPoint = segmentField('setting-control-point', 'Control point', [
    { value: 'palm', label: 'Palm' },
    { value: 'indexTip', label: 'Index tip' },
  ]);
  const fSmoothing = rangeField('setting-smoothing', 'Smoothing', UNIT_RANGE, 0.01);
  const fMinFreq = numberField('setting-min-frequency', 'Minimum pitch', lim.frequency, 'Hz');
  const fMaxFreq = numberField('setting-max-frequency', 'Maximum pitch', lim.frequency, 'Hz');
  const fVibratoRate = rangeField('setting-vibrato-rate', 'Vibrato speed', lim.vibratoRate, 0.1);
  const fVibratoDepth = rangeField('setting-vibrato-depth', 'Vibrato depth', lim.vibratoDepth, 1);
  const fCapture = segmentField('setting-capture-height', 'Capture resolution', [
    { value: '360', label: '360p' },
    { value: '480', label: '480p' },
    { value: '720', label: '720p' },
  ]);
  const fDiagnostics = checkField('setting-show-diagnostics', 'Show diagnostics panel');
  const fScale = rangeField('setting-placement-scale', 'Instrument scale', lim.scale, 0.01);
  const fRotationY = rangeField('setting-rotation-y', 'Turn (yaw)', lim.rotation, 0.01);
  const fRotationX = rangeField('setting-rotation-x', 'Tilt (pitch)', lim.rotation, 0.01);

  const drawerClose = makeButton('settings-close', 'Close settings', 'close', 'btn--icon');
  const group = (title: string, ...rows: HTMLElement[]): HTMLElement =>
    el('section', { class: 'group' }, el('h3', { class: 'section__title', text: title }), ...rows);

  const shortcutList = el(
    'dl',
    { class: 'shortcuts' },
    ...SHORTCUTS.flatMap(([key, description]) => [
      el('dt', { text: key }),
      el('dd', { text: description }),
    ]),
  );

  const advanced = el(
    'details',
    { class: 'group group--advanced', testid: 'settings-advanced' },
    el('summary', { class: 'section__title', text: 'Advanced' }),
    fInvertPitch.row,
    fInvertVolume.row,
    fControlPoint.row,
    fSmoothing.row,
    fMinFreq.row,
    fMaxFreq.row,
    fVibratoRate.row,
    fVibratoDepth.row,
    fCapture.row,
    fDiagnostics.row,
    el('h4', { class: 'section__title', text: 'Placement fine-tune' }),
    fScale.row,
    fRotationY.row,
    fRotationX.row,
  );

  const drawer = el(
    'aside',
    {
      class: 'drawer surface',
      testid: 'settings-drawer',
      attrs: { role: 'dialog', 'aria-modal': 'false', 'aria-labelledby': 'settings-title' },
    },
    div(
      'drawer__head',
      el('h2', { id: 'settings-title', class: 'section__heading', text: 'Settings' }),
      drawerClose.button,
    ),
    group('Sound', fMasterVolume.row, fVibrato.row, fPitchPreset.row),
    group('Playing', fResponse.row, fSensitivity.row, fSwapHands.row),
    group('View', fSkeleton.row, fMirror.row, fReducedMotion.row),
    advanced,
    group('Keyboard', shortcutList),
  );

  // --- modal overlay ---------------------------------------------------------
  const stateTitle = el('h1', {
    id: 'state-title',
    class: 'panel__title',
    testid: 'state-title',
    attrs: { 'aria-live': 'polite' },
  });
  const stateBody = el('p', { class: 'panel__body' });
  const enableCamera = makeButton('enable-camera', 'Enable camera', null, 'btn--primary btn--wide');
  const retryCamera = makeButton('retry-camera', 'Try again', null, 'btn--primary btn--wide');
  const retryError = makeButton('retry-error', 'Try again', null, 'btn--primary btn--wide');

  const introPanel = el(
    'div',
    { class: 'panel__section', testid: 'intro-panel' },
    el(
      'ol',
      { class: 'panel__steps' },
      el('li', { text: 'Stand about an arm’s length from the camera.' }),
      el('li', { text: 'Right hand near the upright antenna sets the pitch.' }),
      el('li', { text: 'Left hand near the loop sets the volume — further away is louder.' }),
    ),
    el('p', { class: 'panel__privacy', text: PRIVACY_TEXT }),
    el('p', { class: 'panel__note', text: MICROPHONE_TEXT }),
    div('panel__actions', enableCamera.button),
  );
  const loadingPanel = el(
    'div',
    { class: 'panel__section', testid: 'loading-panel' },
    el('div', { class: 'spinner', attrs: ariaHidden }),
  );
  const permissionPanel = el(
    'div',
    { class: 'panel__section', testid: 'permission-denied-panel' },
    el('p', { class: 'panel__note', text: PERMISSION_HELP }),
    div('panel__actions', retryCamera.button),
  );
  const errorMessage = el('p', { class: 'panel__error' });
  const errorHint = el('p', { class: 'panel__note', text: 'Reload the page to start over.' });
  const errorPanel = el(
    'div',
    { class: 'panel__section', testid: 'error-panel' },
    errorMessage,
    errorHint,
    div('panel__actions', retryError.button),
  );

  const overlay = el(
    'div',
    {
      class: 'overlay',
      testid: 'state-panel',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'state-title' },
    },
    div(
      'panel surface',
      stateTitle,
      stateBody,
      introPanel,
      loadingPanel,
      permissionPanel,
      errorPanel,
    ),
  );

  const chrome = div(
    'chrome',
    readout,
    diagnostics,
    div('chrome__centre', noHandsBadge),
    div('chrome__bottom', hintBar, calibration, transportMore, transport),
    drawer,
    toast,
  );
  const root = div('theremin', stage, chrome, overlay);
  container.append(root);

  // --- presentation state ----------------------------------------------------
  let state: AppState = 'intro';
  let error: AppErrorInfo | null = null;
  let settings: Readonly<Settings> = DEFAULT_SETTINGS;
  let caps: Readonly<UiCapabilities> = {
    canSwitchCamera: false,
    xrAvailable: false,
    xrActive: false,
    audioRunning: false,
    modelReady: false,
  };
  let calibrationModel: Readonly<CalibrationViewModel> | null = null;
  let drawerOpen = false;
  let moreOpen = false;
  let resetTimer: number | null = null;
  let toastTimer: number | null = null;

  // --- settings binding ------------------------------------------------------
  const syncers: ((next: Readonly<Settings>) => void)[] = [];
  const asFixed2 = (value: number): string => value.toFixed(2);
  const asPercent = (value: number): string => `${Math.round(value * 100)}%`;
  const asDegrees = (value: number): string => `${Math.round((value * 180) / Math.PI)} deg`;
  const asHz = (value: number): string => `${value.toFixed(1)} Hz`;
  const asCents = (value: number): string => `${Math.round(value)} cents`;
  // "1.35" tells a player nothing about how far they will have to move their arm.
  const asReach = (value: number): string =>
    value <= 0.7 ? 'Arm-sized' : value >= 1.6 ? 'Wrist-sized' : 'Medium';

  const bindCheck = (field: Field, key: SettingKey<boolean>): void => {
    on(field.input, 'change', () => callbacks.onSettingChange(key, field.input.checked));
    syncers.push((next) => {
      field.input.checked = next[key];
    });
  };

  const bindRange = (
    field: RangeField,
    format: (value: number) => string,
    read: (next: Readonly<Settings>) => number,
    write: (value: number) => void,
  ): void => {
    on(field.input, 'input', () => {
      const value = Number(field.input.value);
      field.output.textContent = format(value);
      write(value);
    });
    syncers.push((next) => {
      const value = read(next);
      field.input.value = String(value);
      field.output.textContent = format(value);
    });
  };

  const bindSetting = (field: RangeField, key: SettingKey<number>, fmt = asFixed2): void =>
    bindRange(
      field,
      fmt,
      (next) => next[key],
      (value) => callbacks.onSettingChange(key, value),
    );

  const bindPlacement = (field: RangeField, key: keyof PlacementSettings, fmt = asFixed2): void =>
    bindRange(
      field,
      fmt,
      (next) => next.placement[key],
      (value) => {
        const patch: Partial<PlacementSettings> = {};
        patch[key] = value;
        callbacks.onPlacementChange(patch);
      },
    );

  // Number inputs commit on `change`, not `input`, so half-typed values are never applied.
  const bindNumber = (field: Field, key: SettingKey<number>): void => {
    on(field.input, 'change', () => {
      const value = Number(field.input.value);
      if (Number.isFinite(value)) callbacks.onSettingChange(key, value);
    });
    syncers.push((next) => {
      field.input.value = String(Math.round(next[key]));
    });
  };

  const bindSegment = (
    field: SegmentField,
    read: (next: Readonly<Settings>) => string,
    write: (value: string) => void,
  ): void => {
    for (const input of field.inputs) {
      on(input, 'change', () => {
        if (input.checked) write(input.value);
      });
    }
    syncers.push((next) => {
      const value = read(next);
      for (const input of field.inputs) input.checked = input.value === value;
    });
  };

  bindCheck(fSwapHands, 'swapHands');
  bindCheck(fInvertPitch, 'invertPitch');
  bindCheck(fInvertVolume, 'invertVolume');
  bindCheck(fMirror, 'mirrorPreview');
  bindCheck(fSkeleton, 'showSkeleton');
  bindCheck(fReducedMotion, 'reducedMotion');
  bindCheck(fDiagnostics, 'showDiagnostics');
  bindSetting(fSensitivity, 'sensitivity', asReach);
  bindSetting(fSmoothing, 'smoothing');
  bindSetting(fMasterVolume, 'masterVolume', asPercent);
  bindSetting(fVibratoRate, 'vibratoRate', asHz);
  bindSetting(fVibratoDepth, 'vibratoDepth', asCents);
  bindNumber(fMinFreq, 'minFrequency');
  bindNumber(fMaxFreq, 'maxFrequency');
  bindPlacement(fScale, 'scale');
  bindPlacement(fRotationY, 'rotationY', asDegrees);
  bindPlacement(fRotationX, 'rotationX', asDegrees);
  bindSegment(
    fControlPoint,
    (next) => next.controlPoint,
    (value) => callbacks.onSettingChange('controlPoint', value as Settings['controlPoint']),
  );
  bindSegment(
    fCapture,
    (next) => String(next.captureHeight),
    (value) =>
      callbacks.onSettingChange('captureHeight', Number(value) as Settings['captureHeight']),
  );
  bindSegment(
    fVibrato,
    (next) => next.vibratoMode,
    (value) => callbacks.onSettingChange('vibratoMode', value as VibratoMode),
  );
  bindSegment(fPitchPreset, pitchPresetOf, (value) => {
    const preset = PITCH_PRESETS.find((entry) => entry.value === value);
    // Both ends in one patch: applied separately, the sanitizer would see a moment where the
    // new minimum sits above the old maximum and correct one of them out from under us.
    if (preset) callbacks.onSettingsPatch({ minFrequency: preset.min, maxFrequency: preset.max });
  });
  bindSegment(fResponse, responsePresetOf, (value) => {
    const preset = RESPONSE_PRESETS.find((entry) => entry.value === value);
    if (preset) callbacks.onSettingChange('smoothing', preset.smoothing);
  });

  // The pitch-range hint doubles as the readout of what the preset actually chose, so the
  // exact numbers are never hidden — just not in the way.
  const pitchHint = fPitchPreset.hint;
  syncers.push((next) => {
    if (!pitchHint) return;
    setText(
      pitchHint,
      `${Math.round(next.minFrequency)} Hz to ${Math.round(next.maxFrequency)} Hz — ` +
        'how high and low the instrument can sing.',
    );
  });

  // --- wiring ----------------------------------------------------------------
  const disarmReset = (): void => {
    if (resetTimer !== null) clearTimeout(resetTimer);
    resetTimer = null;
    setText(resetBtn.label, 'Reset');
    resetBtn.button.removeAttribute('data-armed');
  };

  const toggleDrawer = (open: boolean): void => {
    drawerOpen = open;
    if (open) moreOpen = false;
    callbacks.onToggleSettings();
    refresh();
  };

  const toggleMore = (open: boolean): void => {
    moreOpen = open;
    if (open) drawerOpen = false;
    refresh();
  };

  on(startBtn.button, 'click', () => callbacks.onStartInstrument());
  on(stopBtn.button, 'click', () => callbacks.onStopInstrument());
  on(muteBtn.button, 'click', () => callbacks.onToggleMute());
  on(switchBtn.button, 'click', () => callbacks.onSwitchCamera());
  on(repositionBtn.button, 'click', () => callbacks.onEnterPlacement());
  on(calibrateBtn.button, 'click', () => callbacks.onStartCalibration());
  on(swapBtn.button, 'click', () => callbacks.onSwapHands());
  on(settingsBtn.button, 'click', () => toggleDrawer(!drawerOpen));
  on(moreBtn.button, 'click', () => toggleMore(!moreOpen));
  on(drawerClose.button, 'click', () => toggleDrawer(false));
  on(diagBtn.button, 'click', () => callbacks.onToggleDiagnostics());
  on(xrBtn.button, 'click', () => (caps.xrActive ? callbacks.onExitXr() : callbacks.onEnterXr()));
  on(placementDone.button, 'click', () => callbacks.onConfirmPlacement());
  on(placementReset.button, 'click', () => callbacks.onResetPlacement());
  on(calibAdvance.button, 'click', () => callbacks.onCalibrationAdvance());
  on(calibCancel.button, 'click', () => callbacks.onCalibrationCancel());
  on(enableCamera.button, 'click', () => callbacks.onRequestCamera());
  on(retryCamera.button, 'click', () => callbacks.onRequestCamera());
  on(retryError.button, 'click', () => callbacks.onRetry());
  // Destructive and one press away from a live performance, so it needs a confirmation.
  on(resetBtn.button, 'click', () => {
    if (resetTimer !== null) {
      disarmReset();
      callbacks.onResetAll();
      return;
    }
    setText(resetBtn.label, 'Confirm reset?');
    resetBtn.button.setAttribute('data-armed', 'true');
    resetTimer = window.setTimeout(disarmReset, RESET_ARM_MS);
  });

  /**
   * Keyboard shortcuts.
   *
   * Playing needs both hands in the air, so the transport has to be reachable without
   * aiming at anything. Everything here is a single unmodified key, ignored while a form
   * control has focus so typing a frequency never starts the instrument.
   */
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
      // Escape still has to work from inside the drawer.
      if (event.key !== 'Escape') return;
    }
    if (event.key === 'Escape') {
      if (drawerOpen) toggleDrawer(false);
      else if (moreOpen) toggleMore(false);
      return;
    }
    if (!CHROME_STATES.has(state)) return;

    switch (event.key.toLowerCase()) {
      case ' ':
        if (caps.audioRunning) callbacks.onStopInstrument();
        else callbacks.onStartInstrument();
        break;
      case 'm':
        callbacks.onToggleMute();
        break;
      case 's':
        callbacks.onSwapHands();
        break;
      case 'c':
        if (caps.modelReady) callbacks.onStartCalibration();
        break;
      case 'd':
        callbacks.onToggleDiagnostics();
        break;
      default:
        return;
    }
    event.preventDefault();
  };
  document.addEventListener('keydown', onKeyDown);
  cleanups.push(() => document.removeEventListener('keydown', onKeyDown));

  // --- derived rendering (everything that is not per-frame) ------------------
  function refresh(): void {
    const panel = PANEL_FOR_STATE[state];
    const chromeVisible = CHROME_STATES.has(state);
    const copy = COPY[state];

    root.dataset['state'] = state;
    root.dataset['modelReady'] = String(caps.modelReady);
    root.classList.toggle('is-reduced-motion', settings.reducedMotion);
    stage.classList.toggle('is-placing', state === 'placement');

    setText(stateTitle, copy.title);
    setText(stateBody, copy.body);
    overlay.hidden = panel === null;
    introPanel.hidden = panel !== 'intro';
    loadingPanel.hidden = panel !== 'loading';
    permissionPanel.hidden = panel !== 'permission';
    errorPanel.hidden = panel !== 'error';
    if (panel === 'error') {
      // An unsupported camera is always retryable in place; a generic error follows its flag.
      const recoverable = state === 'cameraUnsupported' || (error?.recoverable ?? true);
      setText(errorMessage, error?.message ?? copy.body);
      setText(retryError.label, state === 'cameraUnsupported' ? 'Retry' : 'Try again');
      retryError.button.hidden = !recoverable;
      errorHint.hidden = recoverable;
    }

    readout.hidden = !chromeVisible;
    transport.hidden = !chromeVisible;
    transportMore.hidden = !(chromeVisible && moreOpen);
    hintBar.hidden = state !== 'placement';
    noHandsBadge.hidden = state !== 'noHands';
    calibration.hidden = state !== 'calibrating' && calibrationModel === null;
    diagnostics.hidden = !(chromeVisible && settings.showDiagnostics);
    drawer.hidden = !(chromeVisible && drawerOpen);
    tagLayer.hidden = !chromeVisible;
    vibratoRow.hidden = settings.vibratoMode !== 'hand';

    startBtn.button.hidden = caps.audioRunning;
    stopBtn.button.hidden = !caps.audioRunning;
    switchBtn.button.disabled = !caps.canSwitchCamera;
    setText(muteBtn.label, settings.muted ? 'Unmute' : 'Mute');
    muteBtn.button.title = settings.muted ? 'Unmute' : 'Mute';
    setPressed(muteBtn.button, settings.muted);
    setPressed(settingsBtn.button, drawerOpen);
    setPressed(moreBtn.button, moreOpen);
    setExpanded(settingsBtn.button, drawerOpen);
    setExpanded(moreBtn.button, moreOpen);
    setPressed(diagBtn.button, settings.showDiagnostics);
    setPressed(swapBtn.button, settings.swapHands);

    if (caps.xrAvailable) {
      if (!xrBtn.button.isConnected) transportMore.insertBefore(xrBtn.button, resetBtn.button);
      setText(xrBtn.label, caps.xrActive ? 'Exit AR' : 'Enter AR');
      setPressed(xrBtn.button, caps.xrActive);
    } else {
      xrBtn.button.remove();
    }
  }

  // --- per-frame cells -------------------------------------------------------
  const rNote = textCell(noteEl);
  const rCents = cell(centsEl, (v) => (v > 0 ? `+${v}` : String(v)));
  const rHz = cell(freqEl, formatHz);
  const rVolume = cell(volumeEl);
  const rHands = cell(handCountEl);
  const dRender = cell(dRenderFps);
  const dInfFps = cell(dInferenceFps);
  const dInfMs = cell(dInferenceMs, formatMs);
  const dHandCount = cell(dHands);
  const dTierText = textCell(dTier);
  const dModeText = textCell(dMode);
  const dTarget = cell(dTargetHz);
  const dLatencyMs = cell(dLatency, formatMs);
  const dDroppedCount = cell(dDropped);
  const cTitle = textCell(calibTitle);
  const cInstruction = textCell(calibInstruction);
  const cStep = textCell(calibStep);
  const cSamples = cell(calibSamples, (v) => `${v} samples`);
  let lastTune = Number.NaN;
  let lastLevel = -1;
  let lastVibrato = -1;
  let lastPitchHand: boolean | null = null;
  let lastVolumeHand: boolean | null = null;
  let lastSounding: boolean | null = null;
  let lastWidth = -1;
  let lastHeight = -1;
  let lastProgress = -1;
  let lastCanAdvance: boolean | null = null;
  const lastTagPosition: Record<HandTag['role'], string> = { pitch: '', volume: '' };
  const seenTags = new Set<HandTag['role']>();

  const surfaces: UiSurfaces = { root, video, canvas, stage };
  refresh();
  for (const sync of syncers) sync(settings);

  return {
    surfaces,

    setState(next: AppState, nextError: AppErrorInfo | null): void {
      state = next;
      error = nextError;
      refresh();
    },

    setSettings(next: Readonly<Settings>): void {
      settings = next;
      for (const sync of syncers) sync(next);
      refresh();
    },

    setCapabilities(next: Readonly<UiCapabilities>): void {
      caps = next;
      refresh();
    },

    /** Hot path: called every animation frame, so nothing is written unless it changed. */
    setReadout(next: Readonly<LiveReadout>): void {
      const cents = Math.round(clamp(next.cents, -50, 50));
      const volume = Math.round(clamp(next.volume, 0, 1) * 100);
      rNote(next.note);
      rCents(cents);
      rHz(Math.round(next.frequency * 10) / 10);
      rVolume(volume);
      rHands(next.handCount);
      if (cents !== lastTune) {
        lastTune = cents;
        tuner.style.setProperty('--tune', (cents / 50).toFixed(3));
      }
      if (volume !== lastLevel) {
        lastLevel = volume;
        meter.style.setProperty('--level', (volume / 100).toFixed(2));
      }
      const vibrato = Math.round(clamp(next.vibrato, 0, 1) * 100);
      if (vibrato !== lastVibrato) {
        lastVibrato = vibrato;
        vibratoMeter.style.setProperty('--level', (vibrato / 100).toFixed(2));
      }
      if (next.pitchHandPresent !== lastPitchHand) {
        lastPitchHand = next.pitchHandPresent;
        pitchPill.classList.toggle('is-missing', !next.pitchHandPresent);
        pitchPill.dataset['present'] = String(next.pitchHandPresent);
      }
      if (next.volumeHandPresent !== lastVolumeHand) {
        lastVolumeHand = next.volumeHandPresent;
        volumePill.classList.toggle('is-missing', !next.volumeHandPresent);
        volumePill.dataset['present'] = String(next.volumeHandPresent);
      }
      if (next.sounding !== lastSounding) {
        lastSounding = next.sounding;
        readout.classList.toggle('is-sounding', next.sounding);
      }
    },

    setHandTags(tags: readonly HandTag[]): void {
      seenTags.clear();
      for (const tag of tags) {
        const node = handTagNodes[tag.role];
        if (!node) continue;
        seenTags.add(tag.role);
        // Rounded to a tenth of a percent: sub-pixel churn would rewrite style every frame
        // for no visible difference.
        const position = `${(tag.u * 100).toFixed(1)}%,${(tag.v * 100).toFixed(1)}%`;
        if (lastTagPosition[tag.role] !== position) {
          lastTagPosition[tag.role] = position;
          node.style.setProperty('--x', `${(tag.u * 100).toFixed(1)}%`);
          node.style.setProperty('--y', `${(tag.v * 100).toFixed(1)}%`);
        }
        if (node.hidden) node.hidden = false;
      }
      for (const role of ['pitch', 'volume'] as const) {
        const node = handTagNodes[role];
        if (!seenTags.has(role) && !node.hidden) node.hidden = true;
      }
    },

    setDiagnostics(next: Readonly<DiagnosticsSnapshot>): void {
      dRender(Math.round(next.renderFps));
      dInfFps(Math.round(next.inferenceFps));
      dInfMs(Math.round(next.inferenceMs * 10) / 10);
      dHandCount(next.handCount);
      dTierText(next.tier);
      dModeText(next.inferenceMode);
      dTarget(Math.round(next.targetInferenceHz));
      dLatencyMs(Math.round(next.audioLatencyMs * 10) / 10);
      dDroppedCount(next.droppedFrames);
      if (next.cameraWidth !== lastWidth || next.cameraHeight !== lastHeight) {
        lastWidth = next.cameraWidth;
        lastHeight = next.cameraHeight;
        dResolution.textContent = `${next.cameraWidth}x${next.cameraHeight}`;
      }
    },

    setCalibration(model: Readonly<CalibrationViewModel> | null): void {
      const wasVisible = !calibration.hidden;
      calibrationModel = model;
      const visible = model !== null || state === 'calibrating';
      if (visible !== wasVisible) calibration.hidden = !visible;
      if (model === null) return;

      cTitle(model.title);
      cInstruction(model.instruction);
      cStep(`Step ${Math.min(model.index + 1, model.total)} of ${model.total}`);
      cSamples(model.samples);
      const percent = Math.round(clamp(model.progress, 0, 1) * 100);
      if (percent !== lastProgress) {
        lastProgress = percent;
        calibProgress.style.setProperty('--progress', (percent / 100).toFixed(2));
        calibProgress.setAttribute('aria-valuenow', String(percent));
      }
      if (model.canAdvance !== lastCanAdvance) {
        lastCanAdvance = model.canAdvance;
        calibAdvance.button.disabled = !model.canAdvance;
      }
    },

    notify(message: string | null, tone: 'info' | 'warn' | 'error' = 'info'): void {
      if (toastTimer !== null) clearTimeout(toastTimer);
      toastTimer = null;
      if (message === null) {
        toast.hidden = true;
        toast.textContent = '';
        return;
      }
      toast.textContent = message;
      toast.dataset['tone'] = tone;
      toast.hidden = false;
      toastTimer = window.setTimeout(() => {
        toastTimer = null;
        toast.hidden = true;
        toast.textContent = '';
      }, TOAST_MS);
    },

    dispose(): void {
      for (const off of cleanups) off();
      cleanups.length = 0;
      if (resetTimer !== null) clearTimeout(resetTimer);
      if (toastTimer !== null) clearTimeout(toastTimer);
      resetTimer = null;
      toastTimer = null;
      root.remove();
    },
  };
}
